// Size & Colour masters - size sets (each with its sizes), colours and
// sub-categories, on the administration kit: the list of the picked kind on
// the left, the record on the right. Api/SizeColour/* (handover section 6.2).
//
// A master that is in use cannot be deleted; the server answers 400 with the
// reason, which is shown as is.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategory } from "../../redux/userSetting";
import {
  deleteColour,
  deleteSize,
  deleteSizeSet,
  deleteSubCategory,
  getColours,
  getSizeSets,
  getSubCategories,
  saveColour,
  saveSizeSet,
  saveSubCategory,
} from "../../services/SizeColourApiServices";
import {
  AdminList,
  AdminScreen,
  AxAvatar,
  AxDetailFoot,
  AxEmptyDetail,
  AxField,
  AxFilterSelect,
  AxPill,
  AxPlusIcon,
  AxRefreshIcon,
  AxSectionHead,
  AxSegments,
  AxSelectField,
  AxStatusKeys,
  AxTodoNote,
} from "../../components/adminsitration/ui";

const MySwal = withReactContent(Swal);
const KINDS = [
  { key: "sizeset", label: "Size sets", one: "size set" },
  { key: "colour", label: "Colours", one: "colour" },
  { key: "subcat", label: "Sub-categories", one: "sub-category" },
];
const HEX = /^#[0-9a-f]{6}$/i;
const errMsg = (e, fallback) => e?.response?.data?.message || e?.response?.data?.Message || fallback;
const arr = (x) => (Array.isArray(x) ? x : []);

const blank = {
  sizeset: () => ({ guid: "", code: "", name: "", sortOrder: 0, sizes: [] }),
  colour: () => ({ guid: "", code: "", name: "", hexCode: "#000000", sortOrder: 0 }),
  subcat: (categoryID) => ({ guid: "", categoryID, name: "", shortName: "", sizeSetID: "", sortOrder: 0 }),
};

const SizeColourSetup = () => {
  const dispatch = useDispatch();
  const { branch } = useSelector((state) => state.branchlist);
  const { userId } = useSelector((state) => state.userauth);
  const { categoryList } = useSelector((state) => state.usersetting);
  const enabled = useSelector((state) => state.settings?.branchsettings?.enableSizeColour);

  const [kind, setKind] = useState("sizeset");
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [lists, setLists] = useState({ sizeset: [], colour: [], subcat: [] });
  const [draft, setDraft] = useState(null); // the open record
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const updatedUser = `{${userId?.id}}`;

  /* ------------------------------------------------------------ data */
  const loadSets = useCallback(async () => {
    const [sets, colours] = await Promise.all([getSizeSets().catch(() => []), getColours().catch(() => [])]);
    setLists((l) => ({ ...l, sizeset: arr(sets), colour: arr(colours) }));
  }, []);
  const loadSubs = useCallback(async () => {
    if (!category) return setLists((l) => ({ ...l, subcat: [] }));
    const subs = await getSubCategories(category).catch(() => []);
    setLists((l) => ({ ...l, subcat: arr(subs) }));
  }, [category]);

  useEffect(() => {
    if (branch?.guid) dispatch(fetchCategory(branch.guid));
    loadSets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch?.guid]);
  useEffect(() => {
    if (!category && arr(categoryList).length) setCategory(categoryList[0].guid);
  }, [categoryList, category]);
  useEffect(() => {
    loadSubs();
  }, [loadSubs]);

  const reload = () => Promise.all([loadSets(), loadSubs()]);

  /* ------------------------------------------------------------ list */
  const items = useMemo(() => {
    const s = q.trim().toLowerCase();
    return lists[kind]
      .filter((r) => !s || [r.code, r.name, r.shortName].some((x) => String(x || "").toLowerCase().includes(s)))
      .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0) || String(a.name).localeCompare(String(b.name)));
  }, [lists, kind, q]);
  const setName = (g) => lists.sizeset.find((x) => x.guid === g)?.name || "";

  const confirmLeave = () => !dirty || window.confirm("Discard the unsaved changes?");
  const open = (r) => {
    if (!confirmLeave()) return;
    setDraft(
      kind === "sizeset"
        ? { ...r, sizes: arr(r.sizes).map((z) => ({ ...z })) }
        : kind === "subcat"
          ? { ...r, sizeSetID: r.sizeSetID || "" }
          : { ...r, hexCode: r.hexCode || "#000000" }
    );
    setDirty(false);
  };
  const create = () => {
    if (!confirmLeave()) return;
    if (kind === "subcat" && !category) return toast.error("Pick a category first");
    setDraft(blank[kind](category));
    setDirty(false);
  };
  const pickKind = (k) => {
    if (!confirmLeave()) return;
    setKind(k);
    setDraft(null);
    setDirty(false);
  };
  const set = (k, v) => {
    setDraft((d) => ({ ...d, [k]: v }));
    setDirty(true);
  };

  /* ------------------------------------------------------------ sizes inside a set */
  const setSize = (i, k, v) => {
    setDraft((d) => ({ ...d, sizes: d.sizes.map((z, j) => (j === i ? { ...z, [k]: v } : z)) }));
    setDirty(true);
  };
  const addSize = () => {
    setDraft((d) => ({ ...d, sizes: [...d.sizes, { guid: "", code: "", name: "", isCore: true }] }));
    setDirty(true);
  };
  const moveSize = (i, by) => {
    setDraft((d) => {
      const s = [...d.sizes];
      const j = i + by;
      if (j < 0 || j >= s.length) return d;
      [s[i], s[j]] = [s[j], s[i]];
      return { ...d, sizes: s };
    });
    setDirty(true);
  };
  // a saved size is deleted on the server straight away (refused while in use)
  const removeSize = async (i) => {
    const z = draft.sizes[i];
    if (z.guid) {
      const ok = await MySwal.fire({
        title: `Delete size ${z.code || z.name}?`,
        text: "Sizes used by a variant cannot be deleted.",
        showCancelButton: true,
        confirmButtonText: "Delete",
      });
      if (!ok.isConfirmed) return;
      try {
        await deleteSize({ guid: z.guid, updatedUser });
        toast.success("Size deleted");
      } catch (e) {
        return toast.error(errMsg(e, "Could not delete the size"));
      }
    }
    setDraft((d) => ({ ...d, sizes: d.sizes.filter((_, j) => j !== i) }));
    if (!z.guid) setDirty(true);
    if (z.guid) loadSets();
  };

  /* ------------------------------------------------------------ save / delete */
  const problem = () => {
    if (!draft) return "";
    if (kind !== "subcat" && !String(draft.code || "").trim()) return "Enter a code";
    if (!String(draft.name || "").trim()) return "Enter a name";
    if (kind === "colour" && !HEX.test(draft.hexCode || "")) return "The colour must be #RRGGBB";
    if (kind === "subcat" && !draft.categoryID) return "Pick a category";
    if (kind === "sizeset") {
      if (!draft.sizes.length) return "Add at least one size";
      if (draft.sizes.some((z) => !String(z.code || "").trim())) return "Every size needs a code";
      const codes = draft.sizes.map((z) => String(z.code).trim().toLowerCase());
      if (new Set(codes).size !== codes.length) return "Two sizes have the same code";
    }
    return "";
  };
  const save = async () => {
    const p = problem();
    if (p) return toast.error(p);
    setBusy(true);
    try {
      const guid = draft.guid || undefined;
      let res;
      if (kind === "sizeset")
        res = await saveSizeSet({
          guid,
          code: draft.code.trim(),
          name: draft.name.trim(),
          sortOrder: Number(draft.sortOrder) || 0,
          updatedUser,
          sizes: draft.sizes.map((z, i) => ({
            guid: z.guid || undefined,
            code: String(z.code).trim(),
            name: String(z.name || z.code).trim(),
            isCore: !!z.isCore,
            sortOrder: i + 1,
          })),
        });
      else if (kind === "colour")
        res = await saveColour({
          guid,
          code: draft.code.trim(),
          name: draft.name.trim(),
          hexCode: draft.hexCode.toUpperCase(),
          sortOrder: Number(draft.sortOrder) || 0,
          updatedUser,
        });
      else
        res = await saveSubCategory({
          guid,
          categoryID: draft.categoryID,
          name: draft.name.trim(),
          shortName: (draft.shortName || "").trim(),
          sizeSetID: draft.sizeSetID || null,
          sortOrder: Number(draft.sortOrder) || 0,
          updatedUser,
        });
      toast.success(`${KINDS.find((k) => k.key === kind).one} saved`.replace(/^./, (c) => c.toUpperCase()));
      setDirty(false);
      await reload();
      const g = res?.guid || draft.guid;
      setDraft((d) => (d ? { ...d, guid: g } : d));
      // pick the saved record up again so new sizes get their guids
      if (kind === "sizeset") {
        const fresh = arr(await getSizeSets().catch(() => [])).find((x) => x.guid === g);
        if (fresh) setDraft({ ...fresh, sizes: arr(fresh.sizes).map((z) => ({ ...z })) });
      }
    } catch (e) {
      toast.error(errMsg(e, "Could not save"));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    const one = KINDS.find((k) => k.key === kind).one;
    const ok = await MySwal.fire({
      title: `Delete this ${one}?`,
      text: `${draft.name}. A ${one} in use cannot be deleted.`,
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!ok.isConfirmed) return;
    try {
      const fn = kind === "sizeset" ? deleteSizeSet : kind === "colour" ? deleteColour : deleteSubCategory;
      await fn({ guid: draft.guid, updatedUser });
      toast.success("Deleted");
      setDraft(null);
      setDirty(false);
      reload();
    } catch (e) {
      toast.error(errMsg(e, "Could not delete"));
    }
  };

  /* ------------------------------------------------------------ render */
  const cur = KINDS.find((k) => k.key === kind);
  const detail = draft ? (
    <div className="axa-detailform">
      <div className="axa-dhead">
        {kind === "colour" ? (
          <span className="axsc-swatch big" style={{ background: HEX.test(draft.hexCode) ? draft.hexCode : "transparent" }} />
        ) : (
          <AxAvatar name={draft.name || cur.one} />
        )}
        <div className="axa-dtitle">
          <div className="t">
            <span className="nm">{draft.name || `New ${cur.one}`}</span>
          </div>
          <div className="s">
            {kind === "sizeset"
              ? `${draft.sizes.length} sizes · ${draft.sizes.filter((z) => z.isCore).length} core`
              : kind === "subcat"
                ? arr(categoryList).find((c) => c.guid === draft.categoryID)?.name || ""
                : draft.code}
          </div>
        </div>
        <AxPill state={!draft.guid ? "new" : dirty ? "dirty" : "saved"} />
      </div>

      <div className="axa-tabbody">
        <AxSectionHead>{cur.one}</AxSectionHead>
        <div className="axa-formgrid">
          {kind === "subcat" ? (
            <AxSelectField label="Category" required value={draft.categoryID || ""} onChange={(e) => set("categoryID", e.target.value)}>
              <option value="">Pick a category</option>
              {arr(categoryList).map((c) => (
                <option key={c.guid} value={c.guid}>
                  {c.name}
                </option>
              ))}
            </AxSelectField>
          ) : (
            <AxField label="Code" required>
              <input type="text" className="mono" maxLength={20} value={draft.code || ""} onChange={(e) => set("code", e.target.value)} />
            </AxField>
          )}
          <AxField label="Name" required>
            <input type="text" maxLength={100} value={draft.name || ""} onChange={(e) => set("name", e.target.value)} />
          </AxField>
          {kind === "subcat" && (
            <>
              <AxField label="Short name">
                <input type="text" maxLength={20} value={draft.shortName || ""} onChange={(e) => set("shortName", e.target.value)} />
              </AxField>
              <AxSelectField
                label="Size set"
                hint="Used for the products in this sub-category"
                value={draft.sizeSetID || ""}
                onChange={(e) => set("sizeSetID", e.target.value)}
              >
                <option value="">None</option>
                {lists.sizeset.map((s) => (
                  <option key={s.guid} value={s.guid}>
                    {s.name}
                  </option>
                ))}
              </AxSelectField>
            </>
          )}
          {kind === "colour" && (
            <AxField label="Colour" required hint="#RRGGBB">
              <span className="axsc-hexrow">
                <input
                  type="color"
                  value={HEX.test(draft.hexCode) ? draft.hexCode : "#000000"}
                  onChange={(e) => set("hexCode", e.target.value)}
                  aria-label="Pick the colour"
                />
                <input type="text" className="mono" maxLength={7} value={draft.hexCode || ""} onChange={(e) => set("hexCode", e.target.value)} />
              </span>
            </AxField>
          )}
          <AxField label="Sort order">
            <input type="number" min="0" value={draft.sortOrder ?? 0} onChange={(e) => set("sortOrder", e.target.value)} />
          </AxField>
        </div>

        {kind === "sizeset" && (
          <>
            <AxSectionHead>Sizes, in display order</AxSectionHead>
            <table className="axsc-sizes">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th title="The Broken Size report checks core sizes">Core</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {draft.sizes.map((z, i) => (
                  <tr key={z.guid || `new-${i}`}>
                    <td>
                      <input type="text" className="mono" maxLength={10} value={z.code || ""} onChange={(e) => setSize(i, "code", e.target.value)} aria-label="Size code" />
                    </td>
                    <td>
                      <input type="text" maxLength={50} value={z.name || ""} onChange={(e) => setSize(i, "name", e.target.value)} aria-label="Size name" placeholder={z.code} />
                    </td>
                    <td className="c">
                      <input type="checkbox" checked={!!z.isCore} onChange={(e) => setSize(i, "isCore", e.target.checked)} aria-label="Core size" />
                    </td>
                    <td className="act">
                      <button type="button" className="axa-iconbtn" title="Move up" onClick={() => moveSize(i, -1)} disabled={!i}>
                        ↑
                      </button>
                      <button type="button" className="axa-iconbtn" title="Move down" onClick={() => moveSize(i, 1)} disabled={i === draft.sizes.length - 1}>
                        ↓
                      </button>
                      <button type="button" className="axa-iconbtn" title="Remove" onClick={() => removeSize(i)}>
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" className="axa-btn axa-btn-ghost axsc-add" onClick={addSize}>
              <AxPlusIcon /> Add size
            </button>
          </>
        )}
      </div>

      <AxDetailFoot message={problem() && dirty ? problem() : ""} messageIsError>
        {draft.guid ? (
          <button type="button" className="axa-btn axa-btn-danger" onClick={remove} disabled={busy}>
            Delete
          </button>
        ) : null}
        <button
          type="button"
          className="axa-btn axa-btn-ghost"
          onClick={() => {
            if (!confirmLeave()) return;
            setDraft(null);
            setDirty(false);
          }}
        >
          Close
        </button>
        <button type="button" className="axa-btn axa-btn-primary" onClick={save} disabled={busy || !dirty}>
          {busy ? "Saving…" : "Save"}
        </button>
      </AxDetailFoot>
    </div>
  ) : (
    <AxEmptyDetail title={`Pick a ${cur.one}`}>Or press New to add one.</AxEmptyDetail>
  );

  return (
    <AdminScreen
      title="Size & Colour"
      subtitle="Size sets, colours and sub-categories for products sold in sizes and colours"
      rail={
        enabled === false ? (
          <AxTodoNote>Size &amp; colour is switched off in Settings › General, so no screen offers it yet. You can still prepare the masters.</AxTodoNote>
        ) : null
      }
      tools={
        <button type="button" className="axa-iconbtn" title="Reload" onClick={reload}>
          <AxRefreshIcon />
        </button>
      }
      actions={
        <button type="button" className="axa-btn axa-btn-primary" onClick={create}>
          <AxPlusIcon /> New {cur.one}
        </button>
      }
      searchValue={q}
      onSearchChange={(e) => setQ(e.target.value)}
      onSearchClear={() => setQ("")}
      searchPlaceholder={`Search ${cur.label.toLowerCase()}`}
      searchHint={null}
      filters={
        kind === "subcat" ? (
          <AxFilterSelect
            value={category}
            onChange={(e) => {
              if (!confirmLeave()) return;
              setCategory(e.target.value);
              setDraft(null);
              setDirty(false);
            }}
            placeholder="Category"
            options={arr(categoryList).map((c) => ({ value: c.guid, label: c.name }))}
          />
        ) : null
      }
      segments={<AxSegments value={kind} onChange={pickKind} items={KINDS.map((k) => ({ ...k, count: lists[k.key].length }))} />}
      list={
        <AdminList
          items={items}
          keyOf={(r, i) => r.guid || i}
          selectedKey={draft?.guid}
          onSelect={open}
          emptyTitle={lists[kind].length ? "Nothing matches" : `No ${cur.label.toLowerCase()} yet`}
          emptyHint={lists[kind].length ? "Try a shorter search." : `Press New ${cur.one} to add the first one.`}
          renderRow={(r) =>
            kind === "sizeset"
              ? {
                  name: r.name,
                  sub: (
                    <>
                      <span className="mono">{r.code}</span> · {arr(r.sizes).map((z) => z.code).join(" ")}
                    </>
                  ),
                  right: <span className="axa-chip">{arr(r.sizes).length}</span>,
                }
              : kind === "colour"
                ? {
                    name: r.name,
                    sub: <span className="mono">{r.code} · {r.hexCode}</span>,
                    right: <span className="axsc-swatch" style={{ background: r.hexCode }} />,
                  }
                : {
                    name: r.name,
                    sub: [r.shortName, setName(r.sizeSetID) && `sizes: ${setName(r.sizeSetID)}`].filter(Boolean).join(" · "),
                  }
          }
        />
      }
      detail={detail}
      status={<AxStatusKeys keys={[{ k: "Core", t: "sizes the Broken Size report checks" }]} right={`${items.length} ${cur.label.toLowerCase()}`} />}
    />
  );
};

export default SizeColourSetup;
